import { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react'
import { Globe, FolderOpen, X, RefreshCw, Keyboard, Eye, ArrowLeft, ArrowRight, ExternalLink, AlertTriangle, Search, ChevronDown, ChevronRight, BookOpen, Bot, Terminal as TerminalIcon, GitBranch } from 'lucide-react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { ClipboardAddon } from '@xterm/addon-clipboard'
import { SearchAddon } from '@xterm/addon-search'
import { SerializeAddon } from '@xterm/addon-serialize'
import '@xterm/xterm/css/xterm.css'
import type { PanelType } from './PanelToggle'
import { cn } from '../../lib/utils'
import TerminalSearchBar from './TerminalSearchBar'
import FileBrowserPanel from './FileBrowserPanel'
import DocsPanel from './DocsPanel'
import GitPanel from './GitPanel'
import TerminalTabs from './TerminalTabs'
import ClaudePanel from './ClaudePanel'
import { useSettings } from '../../hooks/useSettings'
import { createTerminalOptions, getBackgroundColor, getTerminalTheme, mapCursorStyle, setupBellHandler, loadWebGLAddon } from '../../lib/terminalConfig'
import { useVisualViewport } from '../../hooks/useVisualViewport'
import { MobileKeyboardBar } from './MobileKeyboardBar'
import { withMountBase } from '../../lib/mountBase'

type TabId = 'main' | 'browser' | 'files' | 'preview' | 'docs' | 'git'

interface MobileWorkspaceLayoutProps {
  activePanels: Set<PanelType>
  onPanelClose: (panel: PanelType) => void
  cwd?: string
  browserUrl?: string
  appName?: string  // App name for per-app workspace container routing
  tmuxSession?: string  // Specific tmux session to attach to
  requestedSession?: string  // Session requested by external dropdown (triggers tab switch in TerminalTabs)
  onSessionChange?: (sessionName: string) => void  // Notify parent when active session changes
}

function getTabInfo(id: TabId, claudePanelEnabled = false) {
  if (id === 'main') {
    return claudePanelEnabled
      ? { label: 'Claude', icon: Bot }
      : { label: 'Terminal', icon: TerminalIcon }
  }
  if (id === 'browser') {
    return { label: 'Server Browser', icon: Globe }
  }
  if (id === 'preview') {
    return { label: 'Preview', icon: Eye }
  }
  if (id === 'docs') {
    return { label: 'Docs', icon: BookOpen }
  }
  if (id === 'git') {
    return { label: 'Git', icon: GitBranch }
  }
  return { label: 'Files', icon: FolderOpen }
}

/**
 * Extract app name from cwd path for per-app tmux sessions.
 * Supports formats:
 *   - /home/coder/workspace/apps/{name}
 *   - workspace/apps/{name}
 *   - /workspace/apps/{name}
 */
function extractAppName(cwd?: string): string | null {
  if (!cwd) return null
  const match = cwd.match(/(?:\/home\/coder\/)?workspace\/apps\/([^/]+)/)
  return match ? match[1] : null
}

// Simplified terminal for mobile - no Panel wrapper
// Uses ttyd WebSocket protocol (same as TerminalPanel)
// Now uses per-app tmux sessions for consistency with desktop
// NOTE: Kept as export for potential future use; mobile now uses TerminalTabs
export function MobileTerminal({
  cwd,
  appName: propAppName,
  tmuxSession,
  onSendDataRef,
}: {
  cwd?: string
  appName?: string
  tmuxSession?: string
  onSendDataRef?: React.MutableRefObject<((data: string) => void) | null>
}) {
  const terminalRef = useRef<HTMLDivElement>(null)
  const terminalInstance = useRef<Terminal | null>(null)
  const fitAddon = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const [showSearch, setShowSearch] = useState(false)
  const showSearchRef = useRef(false)
  const searchAddonRef = useRef<SearchAddon | null>(null)
  const serializeAddonRef = useRef<SerializeAddon | null>(null)
  const savedBufferRef = useRef<string | null>(null)
  const { settings } = useSettings()
  const fontSize = settings?.terminal_font_size ?? (settings?.terminal as Record<string, unknown>)?.font_size as number ?? 14
  const terminalTheme = (settings?.terminal as Record<string, unknown>)?.theme as string ?? 'dark'
  const scrollbackLines = (settings?.terminal as Record<string, unknown>)?.scrollback_lines as number ?? 10000
  const cursorStyle = (settings?.terminal as Record<string, unknown>)?.cursor_style as string ?? 'block'
  const viewport = useVisualViewport()

  // Expose sendData function for external keyboard bar
  useEffect(() => {
    if (onSendDataRef) {
      onSendDataRef.current = (data: string) => {
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) {
          // Use TextEncoder for proper UTF-8 encoding of multi-byte characters (e.g., Cyrillic)
          const encoded = new TextEncoder().encode(data)
          const msg = new Uint8Array(encoded.length + 1)
          msg[0] = 48 // '0' = input type
          msg.set(encoded, 1)
          ws.send(msg)
        }
        // Also focus the terminal
        terminalInstance.current?.focus()
      }
    }
    return () => {
      if (onSendDataRef) {
        onSendDataRef.current = null
      }
    }
  }, [onSendDataRef])

  // Keep search ref in sync with state
  useEffect(() => {
    showSearchRef.current = showSearch
  }, [showSearch])

  // Refit terminal when viewport changes (keyboard opens/closes)
  useEffect(() => {
    if (fitAddon.current) {
      setTimeout(() => fitAddon.current?.fit(), 50)
    }
  }, [viewport.height])

  // Update terminal options when settings change (font size, theme, scrollback, cursor)
  useEffect(() => {
    if (terminalInstance.current && fitAddon.current) {
      terminalInstance.current.options.fontSize = fontSize
      terminalInstance.current.options.theme = getTerminalTheme(terminalTheme)
      terminalInstance.current.options.scrollback = scrollbackLines
      terminalInstance.current.options.cursorStyle = mapCursorStyle(cursorStyle)
      fitAddon.current.fit()
    }
  }, [fontSize, terminalTheme, scrollbackLines, cursorStyle])

  useEffect(() => {
    if (!terminalRef.current) return

    const terminal = new Terminal(createTerminalOptions({
      fontSize,
      theme: terminalTheme,
      scrollbackLines,
      cursorStyle,
    }))

    const fit = new FitAddon()
    const webLinks = new WebLinksAddon()

    terminal.loadAddon(fit)
    terminal.loadAddon(webLinks)
    // ClipboardAddon handles OSC 52 sequences from tmux for copy-to-clipboard
    terminal.loadAddon(new ClipboardAddon())

    // Search addon for Cmd+F / Ctrl+F search overlay
    const search = new SearchAddon()
    terminal.loadAddon(search)
    searchAddonRef.current = search

    // Serialize addon for buffer preservation across reconnections
    const serialize = new SerializeAddon()
    terminal.loadAddon(serialize)
    serializeAddonRef.current = serialize

    terminal.open(terminalRef.current)

    terminalInstance.current = terminal
    fitAddon.current = fit

    // Load WebGL renderer (falls back to DOM on context loss or missing support)
    const webgl = loadWebGLAddon(terminal)

    // Register bell handler for tab title flash notification
    const bellDisposable = setupBellHandler(terminal)

    // Try to minimize iOS keyboard accessory bar on xterm's textarea
    const xtermTextarea = terminalRef.current?.querySelector('textarea')
    if (xtermTextarea) {
      xtermTextarea.setAttribute('autocomplete', 'off')
      xtermTextarea.setAttribute('autocorrect', 'off')
      xtermTextarea.setAttribute('autocapitalize', 'off')
      xtermTextarea.setAttribute('spellcheck', 'false')
      xtermTextarea.setAttribute('data-form-type', 'other')
      xtermTextarea.setAttribute('data-lpignore', 'true')
    }

    // Intercept keyboard shortcuts (for external keyboard support):
    // - Cmd+F / Ctrl+F: search bar
    // - Ctrl+V: let browser handle paste (terminals send literal ^V / \x16)
    terminal.attachCustomKeyEventHandler((event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'f' && event.type === 'keydown') {
        setShowSearch(true)
        return false
      }
      if (event.key === 'Escape' && event.type === 'keydown' && showSearchRef.current) {
        setShowSearch(false)
        terminal.focus()
        return false
      }
      // Ctrl+V → let browser handle paste (Cmd+V on Mac already works natively)
      if (event.ctrlKey && !event.metaKey && !event.shiftKey && event.key === 'v' && event.type === 'keydown') {
        return false
      }
      return true
    })

    setTimeout(() => fit.fit(), 100)

    // Build WebSocket URL with optional app name for per-app sessions
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // Use prop appName if provided, otherwise extract from cwd
    const appName = propAppName || extractAppName(cwd)
    const wsPath = appName
      ? withMountBase(`/workspace/app/${encodeURIComponent(appName)}/terminal/ws`, window.location.pathname)
      : withMountBase('/terminal/ws', window.location.pathname)
    const sessionArg = appName && tmuxSession ? `?arg=${encodeURIComponent(tmuxSession)}` : ''
    const wsUrl = `${protocol}//${window.location.host}${wsPath}${sessionArg}`

    const ws = new WebSocket(wsUrl, ['tty'])
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      terminal.focus()

      // Send JSON_DATA init message to spawn the shell
      fit.fit()
      const { cols, rows } = terminal
      const initMsg = JSON.stringify({ columns: cols, rows: rows })
      ws.send(new TextEncoder().encode(initMsg))

      // NOTE: We no longer send cd commands from the frontend.
      // Per-app tmux sessions handle directory and claude automatically:
      // - New session: auto-cd to app dir and start claude
      // - Existing session: just attach (preserves state)

      // Re-sync terminal size after connection
      setTimeout(() => {
        if (fit && ws.readyState === WebSocket.OPEN) {
          fit.fit()
          const { cols, rows } = terminal
          const resizeMsg = JSON.stringify({ columns: cols, rows: rows })
          const resizeData = new Uint8Array(resizeMsg.length + 1)
          resizeData[0] = 49 // '1' = resize type
          for (let i = 0; i < resizeMsg.length; i++) {
            resizeData[i + 1] = resizeMsg.charCodeAt(i)
          }
          ws.send(resizeData)
        }
      }, 300)
    }

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        const data = new Uint8Array(event.data)
        const msgType = data[0]
        if (msgType === 48) { // '0' = output
          terminal.write(data.slice(1))
        }
      } else if (typeof event.data === 'string') {
        const msgType = event.data.charCodeAt(0)
        if (msgType === 48) {
          terminal.write(event.data.slice(1))
        }
      }
    }

    // Serialize buffer on disconnect for future reconnect support
    ws.onclose = () => {
      if (serializeAddonRef.current && terminalInstance.current) {
        try {
          savedBufferRef.current = serializeAddonRef.current.serialize()
        } catch {
          savedBufferRef.current = null
        }
      }
    }

    // Send input to server (uses wsRef.current so reconnect works without re-registering)
    terminal.onData((data) => {
      // Any keyboard input resets scroll bridge state
      if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
      pendingScrollLines = 0
      scrolledUp = false
      scrollOffset = 0
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Use TextEncoder for proper UTF-8 encoding of multi-byte characters (e.g., Cyrillic)
        const encoded = new TextEncoder().encode(data)
        const msg = new Uint8Array(encoded.length + 1)
        msg[0] = 48 // '0' = input type
        msg.set(encoded, 1)
        wsRef.current.send(msg)
      }
    })

    // Handle resize (uses wsRef.current so reconnect works without re-registering)
    terminal.onResize(({ cols, rows }) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // ttyd resize message: '1' + JSON
        const resizeMsg = JSON.stringify({ columns: cols, rows: rows })
        const resizeData = new Uint8Array(resizeMsg.length + 1)
        resizeData[0] = 49 // '1' = resize type
        for (let i = 0; i < resizeMsg.length; i++) {
          resizeData[i + 1] = resizeMsg.charCodeAt(i)
        }
        wsRef.current.send(resizeData)
      }
    })

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => fit.fit(), 50)
    })
    resizeObserver.observe(terminalRef.current)

    // =========================================================================
    // Wheel-to-scroll bridge + touch scroll bridge:
    // tmux mouse is OFF so xterm.js handles selection natively. But xterm.js
    // has no scrollback in alternate screen mode (tmux). We intercept wheel
    // and touch events and send tmux copy-mode key sequences through the WS.
    //
    // Uses vi copy-mode keys ('k'/'j') instead of arrow escape sequences
    // because escape-time=0 in tmux breaks multi-byte \x1b[A sequences.
    // Copy-mode entry (prefix+[) is separated from scroll keys with a delay
    // so tmux has time to switch key tables.
    // =========================================================================
    let scrolledUp = false
    let scrollOffset = 0
    let pendingScrollLines = 0
    let scrollTimer: ReturnType<typeof setTimeout> | null = null

    const sendToWs = (data: string) => {
      const currentWs = wsRef.current
      if (!currentWs || currentWs.readyState !== WebSocket.OPEN || !data) return
      const encoded = new TextEncoder().encode(data)
      const msg = new Uint8Array(encoded.length + 1)
      msg[0] = 48 // '0' = input type
      msg.set(encoded, 1)
      currentWs.send(msg)
    }

    const flushScrollKeys = () => {
      scrollTimer = null
      if (pendingScrollLines === 0) return
      const lines = pendingScrollLines
      pendingScrollLines = 0
      // In vi copy-mode: 'k' = up, 'j' = down (single bytes, no escape issues)
      const key = lines > 0 ? 'k' : 'j'
      sendToWs(key.repeat(Math.abs(lines)))
    }

    const scrollUp = (lines: number) => {
      if (!scrolledUp) {
        // Enter copy-mode first, then send scroll keys after a delay
        sendToWs('\x02[') // Ctrl+B + [ = enter copy-mode
        scrolledUp = true
        pendingScrollLines += lines
        scrollOffset += lines
        if (scrollTimer) clearTimeout(scrollTimer)
        scrollTimer = setTimeout(flushScrollKeys, 80)
        return
      }
      pendingScrollLines += lines
      scrollOffset += lines
      if (scrollTimer) clearTimeout(scrollTimer)
      scrollTimer = setTimeout(flushScrollKeys, 30)
    }

    const scrollDown = (lines: number) => {
      if (!scrolledUp) return
      const toScroll = Math.min(lines, scrollOffset)
      pendingScrollLines -= toScroll
      scrollOffset = Math.max(0, scrollOffset - lines)
      if (scrollOffset <= 0) {
        // Flush any pending keys, then exit copy-mode
        if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
        if (pendingScrollLines !== 0) {
          const key = pendingScrollLines > 0 ? 'k' : 'j'
          sendToWs(key.repeat(Math.abs(pendingScrollLines)))
          pendingScrollLines = 0
        }
        sendToWs('q') // Exit copy-mode
        scrolledUp = false
        scrollOffset = 0
        return
      }
      if (scrollTimer) clearTimeout(scrollTimer)
      scrollTimer = setTimeout(flushScrollKeys, 30)
    }

    // Wheel handler (for desktop/trackpad on mobile browsers)
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
      e.preventDefault()
      const lines = Math.max(1, Math.ceil(Math.abs(e.deltaY) / 40))
      if (e.deltaY < 0) scrollUp(lines)
      else scrollDown(lines)
    }

    // Touch scroll handler (finger swipe on mobile)
    const viewportEl = terminalRef.current?.querySelector('.xterm-viewport') as HTMLElement | null
    let touchStartY = 0
    let lastScrollTime = 0

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartY = e.touches[0].clientY
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const deltaY = touchStartY - e.touches[0].clientY
      const now = Date.now()
      if (Math.abs(deltaY) > 20 && now - lastScrollTime > 50) {
        const lines = Math.ceil(Math.abs(deltaY) / 20)
        if (deltaY > 0) scrollUp(lines)  // Finger moving up = scroll up through history
        else scrollDown(lines)              // Finger moving down = scroll down
        touchStartY = e.touches[0].clientY
        lastScrollTime = now
      }
    }

    // Click exits copy-mode
    const handleClickExitCopyMode = () => {
      if (scrolledUp) {
        if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
        pendingScrollLines = 0
        scrolledUp = false
        scrollOffset = 0
        sendToWs('q')
      }
    }

    const terminalEl = terminalRef.current
    const scrollTarget = viewportEl || terminalEl
    terminalEl?.addEventListener('wheel', handleWheel, { passive: false })
    terminalEl?.addEventListener('mousedown', handleClickExitCopyMode)
    scrollTarget?.addEventListener('touchstart', handleTouchStart, { passive: true })
    scrollTarget?.addEventListener('touchmove', handleTouchMove, { passive: true })

    return () => {
      terminalEl?.removeEventListener('wheel', handleWheel)
      terminalEl?.removeEventListener('mousedown', handleClickExitCopyMode)
      scrollTarget?.removeEventListener('touchstart', handleTouchStart)
      scrollTarget?.removeEventListener('touchmove', handleTouchMove)
      bellDisposable.dispose()
      webgl.dispose()
      searchAddonRef.current = null
      serializeAddonRef.current = null
      resizeObserver.disconnect()
      ws.close()
      terminal.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cwd, fontSize]) // propAppName and tmuxSession intentionally excluded - only read once on mount

  return (
    <div
      className="relative w-full"
      style={{
        backgroundColor: getBackgroundColor(terminalTheme),
        height: viewport.isKeyboardOpen ? viewport.height : '100%',
      }}
    >
      {/* Search toggle button for touch access (no Cmd key on mobile) */}
      {!showSearch && (
        
          <button title="Search text in terminal output"
            onClick={() => setShowSearch(true)}
            className="absolute top-2 left-2 z-20 p-1.5 rounded-lg bg-card/80 border border-border shadow-sm hover:bg-card transition-colors"
          >
            <Search className="w-4 h-4 text-muted-foreground" />
          </button>
        
      )}
      {showSearch && searchAddonRef.current && (
        <TerminalSearchBar
          searchAddon={searchAddonRef.current}
          onClose={() => {
            setShowSearch(false)
            terminalInstance.current?.focus()
          }}
        />
      )}
      <div
        ref={terminalRef}
        className="w-full h-full"
        style={{
          // Allow touch scrolling to work with xterm.js
          touchAction: 'auto',
        }}
      />
    </div>
  )
}

// Simplified Xpra iframe for mobile - no Panel wrapper
// Memoized to prevent re-renders when viewport changes (which would reload the iframe)
const MobileXpra = memo(function MobileXpra({ baseUrl, startUrl }: { baseUrl: string; startUrl?: string }) {
  const [key] = useState(0)
  // Track if we've already used the startUrl to prevent duplicate tab opens
  const startUrlUsedRef = useRef(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hiddenInputRef = useRef<HTMLInputElement>(null)

  const getXpraUrl = useCallback(() => {
    const params = new URLSearchParams({
      'auto': 'true',
      'keyboard': 'false',
      'keyboard_button': 'false',
      // Prevent focus lock issues
      'pointer_grab': 'false',
      'keyboard_grab': 'false',
      'steal_focus': 'false',
      'floating_menu': 'false',
      'clipboard': 'true',
      'clipboard_direction': 'both',
      'encoding': 'auto',
      'notifications': 'false',
      'sound': 'false',
      'touch': 'true',  // Enable touch scrolling in Xpra
      '_t': Date.now().toString(),
    })
    // Pass start URL only on first use to prevent duplicate tabs
    if (startUrl && !startUrlUsedRef.current) {
      params.set('start_url', startUrl)
      startUrlUsedRef.current = true
    }
    return `${baseUrl}?${params.toString()}`
  }, [baseUrl, startUrl])

  // Handle keyboard input and send to Xpra via postMessage
  const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const value = input.value
    if (value && iframeRef.current?.contentWindow) {
      // Send key events to Xpra
      iframeRef.current.contentWindow.postMessage({
        type: 'xpra-keyboard-input',
        text: value,
      }, '*')
    }
    // Clear the input
    input.value = ''
  }, [])

  return (
    <div className="relative w-full h-full">
      {/* Hidden input for keyboard trigger - positioned in view to work on iOS */}
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="text"
        className="absolute top-0 left-0 w-px h-px opacity-0 pointer-events-none"
        onInput={handleInput}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-form-type="other"
        data-lpignore="true"
        aria-hidden="true"
      />
      {/* Keyboard trigger button - fixed position in top right corner */}
      
        <button title="Show the on-screen keyboard"
          onClick={() => hiddenInputRef.current?.focus()}
          className="absolute top-2 right-2 z-10 p-2 bg-primary/90 rounded-lg shadow-lg min-w-[44px] min-h-[44px] flex items-center justify-center active:bg-primary transition-colors"
        >
          <Keyboard className="w-5 h-5 text-primary-foreground" />
        </button>
      
      <iframe
        ref={iframeRef}
        key={key}
        src={getXpraUrl()}
        className="w-full h-full border-0"
        allow="clipboard-read; clipboard-write"
        title="Xpra"
        style={{ touchAction: 'auto' }}
      />
    </div>
  )
})

// Simplified local browser for mobile - iframe-based preview
function MobilePreviewBrowser({ initialUrl }: { initialUrl?: string }) {
  const [url, setUrl] = useState(initialUrl || '')
  const [inputUrl, setInputUrl] = useState(initialUrl || '')
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [history, setHistory] = useState<string[]>(initialUrl ? [initialUrl] : [])
  const [historyIndex, setHistoryIndex] = useState(initialUrl ? 0 : -1)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const normalizeUrl = useCallback((rawUrl: string): string => {
    let normalized = rawUrl.trim()
    if (!normalized) return ''
    if (!/^https?:\/\//i.test(normalized)) {
      normalized = 'https://' + normalized
    }
    return normalized
  }, [])

  const navigateTo = useCallback((targetUrl: string, addToHistory = true) => {
    const normalized = normalizeUrl(targetUrl)
    if (!normalized) return

    setUrl(normalized)
    setInputUrl(normalized)
    setIsLoading(true)
    setHasError(false)

    if (addToHistory) {
      const newHistory = [...history.slice(0, historyIndex + 1), normalized]
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    }
  }, [normalizeUrl, history, historyIndex])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    navigateTo(inputUrl)
  }, [inputUrl, navigateTo])

  const canGoBack = historyIndex > 0
  const canGoForward = historyIndex < history.length - 1

  const goBack = useCallback(() => {
    if (!canGoBack) return
    const newIndex = historyIndex - 1
    setHistoryIndex(newIndex)
    const targetUrl = history[newIndex]
    setUrl(targetUrl)
    setInputUrl(targetUrl)
    setIsLoading(true)
    setHasError(false)
  }, [canGoBack, historyIndex, history])

  const goForward = useCallback(() => {
    if (!canGoForward) return
    const newIndex = historyIndex + 1
    setHistoryIndex(newIndex)
    const targetUrl = history[newIndex]
    setUrl(targetUrl)
    setInputUrl(targetUrl)
    setIsLoading(true)
    setHasError(false)
  }, [canGoForward, historyIndex, history])

  const refresh = useCallback(() => {
    if (!url) return
    setIsLoading(true)
    setHasError(false)
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src
      iframeRef.current.src = ''
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc
        }
      }, 50)
    }
  }, [url])

  const openInNewTab = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }, [url])

  // Auto-navigate to initialUrl on mount
  useEffect(() => {
    if (initialUrl && !url) {
      navigateTo(initialUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Navigate when initialUrl prop changes (e.g., branch switch)
  useEffect(() => {
    if (initialUrl && initialUrl !== url) {
      navigateTo(initialUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl])

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-background">
        <button
          onClick={goBack}
          disabled={!canGoBack}
          className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 min-w-[36px] min-h-[36px] flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          onClick={goForward}
          disabled={!canGoForward}
          className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 min-w-[36px] min-h-[36px] flex items-center justify-center"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={refresh}
          disabled={!url || isLoading}
          className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 min-w-[36px] min-h-[36px] flex items-center justify-center"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        <form onSubmit={handleSubmit} className="flex-1 mx-1">
          <input
            type="text"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="Enter URL..."
            className="w-full px-2 py-1 text-sm rounded bg-background border border-border focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </form>
        <button
          onClick={openInNewTab}
          disabled={!url}
          className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 min-w-[36px] min-h-[36px] flex items-center justify-center"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 relative overflow-hidden">
        {!url && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            <Eye className="w-10 h-10 mb-3 opacity-50" />
            <p className="text-sm">Enter a URL to preview</p>
          </div>
        )}

        {url && isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
            <RefreshCw className="w-5 h-5 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </div>
        )}

        {url && hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10 p-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500 mb-2" />
            <p className="text-sm text-muted-foreground mb-3 text-center">
              This site cannot be displayed in an embedded frame
            </p>
            <button
              onClick={openInNewTab}
              className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm flex items-center gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </button>
          </div>
        )}

        {url && (
          <iframe
            ref={iframeRef}
            src={url}
            className="w-full h-full border-0"
            onLoad={() => { setIsLoading(false); setHasError(false) }}
            onError={() => { setIsLoading(false); setHasError(true) }}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
            allow="clipboard-read; clipboard-write"
            title="Preview"
            style={{ touchAction: 'auto' }}
          />
        )}
      </div>
    </div>
  )
}

export default function MobileWorkspaceLayout({
  activePanels,
  onPanelClose,
  cwd,
  browserUrl,
  appName,
  tmuxSession,
  requestedSession,
  onSessionChange,
}: MobileWorkspaceLayoutProps) {
  const availableTabs: TabId[] = ['main']
  if (activePanels.has('preview')) availableTabs.push('preview')
  if (activePanels.has('browser')) availableTabs.push('browser')
  if (activePanels.has('files')) availableTabs.push('files')
  if (activePanels.has('docs')) availableTabs.push('docs')
  if (activePanels.has('git')) availableTabs.push('git')

  const { settings } = useSettings()
  const claudePanelEnabled = !!(settings?.dashboard as Record<string, unknown>)?.claude_panel_enabled

  const [activeTab, setActiveTab] = useState<TabId>('main')
  const [refreshKey, setRefreshKey] = useState(0)
  const viewport = useVisualViewport()
  const terminalSendDataRef = useRef<((data: string) => void) | null>(null)

  // Show keyboard bar when keyboard is open and terminal tab is active (not for Claude panel)
  const showKeyboardBar = viewport.isKeyboardOpen && activeTab === 'main' && !claudePanelEnabled

  // Track active terminal session for session-aware file browser path
  const [activeSession, setActiveSessionState] = useState(tmuxSession || 'main')

  // Wrapper that notifies both local state and parent
  const setActiveSession = useCallback((session: string) => {
    setActiveSessionState(session)
    onSessionChange?.(session)
  }, [onSessionChange])

  // Sync activeSession when parent requests a session switch via the branch dropdown.
  useEffect(() => {
    if (requestedSession && requestedSession !== activeSession) {
      setActiveSessionState(requestedSession)
      onSessionChange?.(requestedSession)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSession])

  // Derive effective cwd based on session (worktrees are in different paths)
  const effectiveCwd = useMemo(() => {
    if (appName && activeSession && activeSession !== 'main') {
      return `/home/coder/workspace/worktrees/${activeSession}`
    }
    return cwd
  }, [appName, activeSession, cwd])

  // If active tab is removed, switch to main
  if (!availableTabs.includes(activeTab)) {
    setActiveTab('main')
  }

  const handleClose = useCallback((tabId: TabId) => {
    if (tabId === 'browser') onPanelClose('browser')
    if (tabId === 'files') onPanelClose('files')
    if (tabId === 'preview') onPanelClose('preview')
    if (tabId === 'docs') onPanelClose('docs')
    if (tabId === 'git') onPanelClose('git')
    setActiveTab('main')
  }, [onPanelClose])

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1)
  }, [])

  const renderPanel = useCallback((id: TabId) => {
    if (id === 'main') {
      if (claudePanelEnabled) {
        // Claude panel — structured WebSocket communication with Claude Code CLI
        return (
          <ClaudePanel
            appName={appName || ''}
            closeable={false}
          />
        )
      }
      // TerminalTabs provides multi-tab terminal experience with activity badges
      return (
        <TerminalTabs
          appName={appName || ''}
          initialSession={tmuxSession}
          onSendDataRef={terminalSendDataRef}
          onSessionChange={setActiveSession}
          requestedSession={requestedSession}
        />
      )
    }

    if (id === 'browser') {
      // Use per-app browser route if appName is available
      const browserBaseUrl = withMountBase(
        appName
          ? `/workspace/app/${encodeURIComponent(appName)}/browser/`
          : '/browser/'
      )
      return <MobileXpra key={`browser-${refreshKey}`} baseUrl={browserBaseUrl} startUrl={browserUrl} />
    }

    if (id === 'files') {
      return (
        <FileBrowserPanel
          key={`files-${activeSession}`}
          standalone={true}
          initialPath={effectiveCwd}
          appName={appName}
        />
      )
    }

    if (id === 'preview') {
      return <MobilePreviewBrowser key={`preview-${refreshKey}`} initialUrl={browserUrl} />
    }

    if (id === 'docs') {
      return (
        <DocsPanel
          key={`docs-${activeSession}`}
          appName={appName || ''}
          rootPath={effectiveCwd}
        />
      )
    }

    if (id === 'git') {
      return appName ? <GitPanel appName={appName} /> : null
    }

    return null
  }, [effectiveCwd, refreshKey, browserUrl, appName, activeSession, claudePanelEnabled, tmuxSession, requestedSession, setActiveSession])

  const showTabs = availableTabs.length > 1

  // Mobile panel tab collapse state
  const [mobilePanelTabsCollapsed, setMobilePanelTabsCollapsed] = useState(() => {
    try { return localStorage.getItem('mobile-panel-tabs-collapsed') === 'true' } catch { return false }
  })
  const toggleMobilePanelTabs = useCallback(() => {
    setMobilePanelTabsCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem('mobile-panel-tabs-collapsed', String(next)) } catch { /* ignore */ }
      return next
    })
  }, [])

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar - only show if multiple panels */}
      {showTabs && mobilePanelTabsCollapsed ? (
        <div className="flex items-center border-b border-border bg-background h-7 flex-shrink-0">
          <button
            onClick={toggleMobilePanelTabs}
            className="flex items-center gap-1 px-2 h-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="Expand panel tabs"
          >
            <ChevronRight className="w-3 h-3" />
            {(() => { const info = getTabInfo(activeTab, claudePanelEnabled); return <info.icon className="w-3 h-3" /> })()}
            <span>{getTabInfo(activeTab, claudePanelEnabled).label}</span>
          </button>
        </div>
      ) : showTabs ? (
        <div className="flex border-b border-border bg-background flex-shrink-0">
          {availableTabs.map((tabId) => {
            const info = getTabInfo(tabId, claudePanelEnabled)
            const isActive = activeTab === tabId
            const isCloseable = tabId !== 'main'

            return (
              <div
                key={tabId}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1 text-sm font-medium transition-colors relative',
                  isActive
                    ? 'text-primary border-b-2 border-primary bg-secondary/30'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/20'
                )}
              >
                <button
                  onClick={() => setActiveTab(tabId)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 flex-1"
                >
                  <info.icon className="w-4 h-4" />
                  <span>{info.label}</span>
                </button>

                {/* Close button for non-main tabs - 44px minimum touch target */}
                {isCloseable && isActive && (
                  
                    <button title={`Close the ${info.label} panel`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleClose(tabId)
                      }}
                      className="p-2 -m-1 min-w-[44px] min-h-[44px] flex items-center justify-center rounded hover:bg-secondary/50 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  
                )}

                {/* Refresh button for browser tab - 44px minimum touch target */}
                {isActive && tabId === 'browser' && (
                  
                    <button title="Reload the panel content"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRefresh()
                      }}
                      className="p-2 -m-1 min-w-[44px] min-h-[44px] flex items-center justify-center rounded hover:bg-secondary/50 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  
                )}
              </div>
            )
          })}
          {/* Collapse toggle */}
          
            <button title="Minimize the panel tab bar"
              onClick={toggleMobilePanelTabs}
              className="flex items-center justify-center w-8 flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          
        </div>
      ) : null}

      {/* Panel content - no extra header */}
      <div
        className="flex-1 overflow-hidden"
        style={{
          height: viewport.isKeyboardOpen ? viewport.height - (showTabs ? 44 : 0) : undefined,
          paddingBottom: !viewport.isKeyboardOpen ? 'var(--safe-area-bottom)' : undefined,
        }}
      >
        {renderPanel(activeTab)}
      </div>

      {/* Mobile keyboard bar - shown above keyboard when terminal is active */}
      <MobileKeyboardBar
        visible={showKeyboardBar}
        keyboardHeight={viewport.keyboardHeight}
        onSendData={(data) => terminalSendDataRef.current?.(data)}
      />
    </div>
  )
}
